// The web Blog composer (Figma eWr6sqSGfDufrRNUMjbtK4 `Web / Blog / Composer` 3142:966 empty, 3142:1042 filled, 3142:1158 errors):
// create a BLOG post, or edit one from its menu. Like the Book composer, Post is never disabled: a tap with missing or
// invalid fields shows the red reason under each, and a change to a field clears its error. Paragraphs are cards, not
// numbered; each has its text and its media rows (Photo, Video, Audio, Link) with a caption field. Media goes through the
// wall composer's own paths: images and MP3 through file-stc (`uploadImage`, `uploadAudio`), videos through the app-wide
// upload queue, links through the link resolver, and each row is the wall composer's row.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ImagePlus, Trash2, X } from "lucide-react";
import { ContentError, createPostV2, getPostV2, resolveLink, updatePostV2, type PostV2 } from "../../api/content";
import { AUDIO_ACCEPT, IMAGE_ACCEPT, SvgTooLargeError, uploadAudio, uploadImage } from "../../api/upload";
import { invalidateNewsPosted } from "../../hooks/useWallFeed";
import { uploadQueue, useUploadQueueStore } from "../../store/uploadQueue";
import { enqueueWallVideo, releaseWallVideos, useWallVideoPostStore, videoSendOf } from "../../services/wallVideoPost";
import { useComposerDraftStore } from "../../store/composerDraftStore";
import { contentErrorText } from "../wall/contentErrorText";
import ComposerItemRow from "../wall/ComposerItemRow";
import {
  applyResolved, audioTitle, editTitle, filesFromPicked, newLink, nextKey, resetTitle, storedLinkPreview, titleOver, titleTooLong, withVideoSend,
  type ComposerItem, type FileItem, type LinkItem,
} from "../wall/composerModel";
import { isVideoItem } from "../../player";
import LiveConfirmDialog from "../stream/LiveConfirmDialog";
import {
  MAX_ABSTRACT_WORDS, MAX_ACKNOWLEDGEMENTS, MAX_AUTHOR, MAX_BIBLIOGRAPHY, MAX_CAPTION, MAX_LANGUAGE, MAX_MEDIA, MAX_PARAGRAPH_CHARS,
  MAX_TRANSLATION_LANGUAGE, MAX_TRANSLATOR, addMedia, blogDraftOf, blogErrors, moveMediaDown, moveMediaUp, moveParagraphDown, moveParagraphUp,
  newBlogDraft, toBlogInput, wordCount,
  type BlogDraft, type BlogErrors, type BlogMedia, type BlogParagraphDraft,
} from "./blogDraft";

interface Props {
  /** The BLOG post to edit; null = a new blog. */
  existing: PostV2 | null;
  onClose(): void;
  onSaved(post: PostV2): void;
  resolveDelayMs?: number;
}

type Kind = "photo" | "video" | "audio" | "link";

const LABEL = "text-xs font-medium text-[var(--foreground-muted)]";
const INPUT = "w-full rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2.5 text-[15px] text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)] focus:ring-2 focus:ring-[var(--primary-text)] aria-invalid:border-[var(--destructive)]";
const PILL = "h-9 rounded-full border border-[var(--border-strong)] bg-[var(--bg-surface)] px-4 text-sm font-bold text-[var(--foreground)] disabled:opacity-45 cursor-pointer";
const ICON_BTN = "flex size-7 items-center justify-center rounded-full text-[var(--foreground-muted)] hover:bg-[var(--bg-hover)] disabled:opacity-40 cursor-pointer";

function Err({ id, children }: { id: string; children: ReactNode }) {
  return <p data-testid={`blog-composer-${id}-error`} role="alert" className="m-0 text-xs text-[var(--destructive-text)]">{children}</p>;
}

/** A stored media as a composer row item (done, ready to send again). */
function itemOf(m: BlogMedia): ComposerItem {
  const caption = m.caption || undefined;
  if (m.fileId && (m.type === "PHOTO" || m.type === "VIDEO")) {
    return { key: nextKey(), kind: "file", type: m.type, status: "done", fileId: m.fileId, thumbnailFileId: m.thumbnailFileId, previewUrl: m.previewUrl, caption };
  }
  if (m.fileId) return { key: nextKey(), kind: "file", type: "AUDIO", status: "done", fileId: m.fileId, name: m.title, caption };
  const preview = storedLinkPreview({ type: m.type, url: m.url ?? "", title: m.title ?? null, thumbnailUrl: m.previewUrl ?? null });
  return { key: nextKey(), kind: "link", url: m.url ?? "", status: "ok", preview, title: m.title ?? "", linkTitle: null, titleEdited: false, caption };
}

/** The media as the request will carry it, from its row item (what uploaded, or what the link resolved to). */
function mediaOf(m: BlogMedia, it: ComposerItem): BlogMedia {
  if (it.kind === "file") {
    const audio = it.type === "AUDIO";
    return { ...m, fileId: it.fileId, thumbnailFileId: it.thumbnailFileId, url: undefined, type: audio ? "AUDIO_URL" : (it.type as BlogMedia["type"]), title: audio ? audioTitle(it.name) || undefined : undefined };
  }
  const url = it.preview?.url ?? it.url;
  return { ...m, fileId: undefined, thumbnailFileId: undefined, url, type: it.preview?.type ?? m.type, title: titleOver(it) || it.title.trim() === "" ? undefined : it.title.trim() };
}

/** An edit opened from a list copy (no paragraphs) reads the post again before showing the form. */
export default function BlogComposerModal(props: Props) {
  const { t } = useTranslation();
  const ex = props.existing;
  const needsFull = ex != null && ex.blog?.paragraphs == null;
  const full = useQuery({ queryKey: ["postEditV2", ex?.id ?? null], queryFn: () => getPostV2(ex!.id), enabled: needsFull, retry: false, gcTime: 0 });
  if (!needsFull) return <BlogComposer {...props} />;
  if (full.data?.blog?.paragraphs) return <BlogComposer {...props} existing={full.data} />;
  const failed = full.isError || (full.data != null && full.data.blog?.paragraphs == null);
  return (
    <div role="dialog" aria-modal="true" aria-label={t("blog_composer_edit", "Edit blog")} data-testid="blog-composer-loading"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]">
      <div className="flex w-[400px] max-w-full flex-col items-center gap-3 rounded-2xl bg-[var(--bg-surface)] p-6 shadow-xl">
        {failed ? (
          <>
            <p role="alert" className="text-sm text-[var(--destructive-text)]">
              {contentErrorText(t, full.error instanceof ContentError ? full.error.code : "UNKNOWN", t("wall_error_UNKNOWN", "Something went wrong — try again"))}
            </p>
            <button type="button" className="text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)]" onClick={props.onClose}>{t("wall_close", "Close")}</button>
          </>
        ) : (
          <span aria-busy="true" className="text-sm text-[var(--foreground-muted)]">…</span>
        )}
      </div>
    </div>
  );
}

function BlogComposer({ existing, onClose, onSaved, resolveDelayMs = 500 }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const initial = useRef<BlogDraft>(existing ? blogDraftOf(existing) : newBlogDraft());
  const [draft, setDraft] = useState<BlogDraft>(initial.current);
  /** The row item of every media, by `BlogMedia.key`: its upload or link state (stored media start as done rows). */
  const initialRows = useRef<Record<string, ComposerItem>>({});
  const [rows, setRows] = useState<Record<string, ComposerItem>>(() => {
    const out: Record<string, ComposerItem> = {};
    for (const pg of initial.current.paragraphs) for (const m of pg.media) out[m.key] = itemOf(m);
    initialRows.current = out;
    return out;
  });
  const [shown, setShown] = useState<BlogErrors>({});
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverFailed, setCoverFailed] = useState<false | "failed" | "svg">(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const coverInput = useRef<HTMLInputElement>(null);
  const mediaInput = useRef<HTMLInputElement>(null);
  const pick = useRef<{ paragraph: string; kind: Kind } | null>(null);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  // One id per composer session: its videos' queue target.
  const [draftId] = useState(() => crypto.randomUUID());
  const queueItems = useUploadQueueStore((s) => s.items);
  const readyVideos = useWallVideoPostStore((s) => s.ready);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // The row as it stands now (a new video follows the upload queue).
  const viewOf = (m: BlogMedia): ComposerItem | undefined => {
    const it = rows[m.key];
    if (!it) return undefined;
    const sent = it.kind === "file" && it.uploadId != null ? withVideoSend(it, videoSendOf(queueItems[it.uploadId]), readyVideos[it.uploadId]) : it;
    return { ...sent, caption: m.caption };
  };
  const allMedia = draft.paragraphs.flatMap((p) => p.media);
  const views = useMemo(
    () => allMedia.map((m) => viewOf(m)).filter((x): x is ComposerItem => x != null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, rows, queueItems, readyVideos],
  );
  const viewsRef = useRef(views);
  viewsRef.current = views;

  // Unmounted with videos still in the queue: they stop (a blog is not queued behind its videos).
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      releaseWallVideos(viewsRef.current.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : [])));
    };
  }, []);
  // A video cancelled from the queue toast leaves its paragraph, as × on its row does.
  useEffect(() => {
    const gone = Object.entries(rows).filter(([, it]) => it.kind === "file" && it.uploadId != null && !readyVideos[it.uploadId] && queueItems[it.uploadId]?.phase === "cancelled").map(([k]) => k);
    if (gone.length === 0) return;
    gone.forEach(removeMedia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, queueItems, readyVideos]);

  const uploading = views.some((it) => it.kind === "file" && it.status === "uploading");
  const dirty = coverBusy || uploading || JSON.stringify(draft) !== JSON.stringify(initial.current) || JSON.stringify(rows) !== JSON.stringify(initialRows.current);
  useEffect(() => {
    useComposerDraftStore.getState().set({ dirty });
  }, [dirty]);
  useEffect(() => () => useComposerDraftStore.getState().reset(), []);
  const pendingDiscard = useComposerDraftStore((s) => s.pending);
  const submittingRef = useRef(false);
  // Close and Discard do nothing while the post is being sent.
  const guardedClose = () => {
    if (submittingRef.current) return;
    useComposerDraftStore.getState().guard(() => { if (!submittingRef.current) onClose(); });
  };
  const guardedCloseRef = useRef(guardedClose);
  guardedCloseRef.current = guardedClose;
  useEffect(() => {
    useComposerDraftStore.getState().set({ requestClose: () => guardedCloseRef.current() });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) guardedCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Edits a field; its shown error goes away at once. */
  function patch(p: Partial<BlogDraft>, field?: keyof BlogErrors) {
    setDraft((d) => ({ ...d, ...p }));
    if (field) setShown((s) => { const { [field]: _gone, ...rest } = s; return rest; });
  }
  const patchParagraph = (key: string, f: (p: BlogParagraphDraft) => BlogParagraphDraft) => {
    setDraft((d) => ({ ...d, paragraphs: d.paragraphs.map((p) => (p.key === key ? f(p) : p)) }));
    setShown((s) => { const { badParagraphs: _b, paragraphs: _p, text: _t, media: _m, ...rest } = s; return rest; });
  };
  const setRow = (key: string, f: (it: ComposerItem) => ComposerItem) => setRows((cur) => (cur[key] ? { ...cur, [key]: f(cur[key]) } : cur));

  function removeMedia(mediaKey: string) {
    const it = rowsRef.current[mediaKey];
    if (it) {
      clearTimeout(timers.current.get(it.key));
      timers.current.delete(it.key);
      if (it.kind === "file" && it.uploadId != null) releaseWallVideos([it.uploadId]);
    }
    setDraft((d) => ({ ...d, paragraphs: d.paragraphs.map((p) => ({ ...p, media: p.media.filter((m) => m.key !== mediaKey) })) }));
    setRows((cur) => { const { [mediaKey]: _gone, ...rest } = cur; return rest; });
  }

  function addRow(paragraph: string, type: BlogMedia["type"], item: ComposerItem): string {
    const key = `m${item.key}`;
    const m: BlogMedia = { key, type, caption: "" };
    patchParagraph(paragraph, (p) => addMedia(p, m));
    setRows((cur) => ({ ...cur, [key]: item }));
    return key;
  }

  function addLink(paragraph: string) {
    addRow(paragraph, "AUDIO_URL", newLink());
  }

  function onUrlChange(mediaKey: string, url: string) {
    const it = rowsRef.current[mediaKey];
    if (!it || it.kind !== "link") return;
    const k = it.key;
    setRow(mediaKey, (cur) => ({ ...cur, url, status: url.trim() ? "checking" : "idle", preview: undefined, error: undefined } as LinkItem));
    clearTimeout(timers.current.get(k));
    if (!url.trim()) return;
    timers.current.set(k, setTimeout(async () => {
      // Applied only while the row still holds the URL it was asked for.
      const same = (cur: ComposerItem) => cur.kind === "link" && cur.url === url;
      try {
        const preview = await resolveLink(url.trim());
        setRow(mediaKey, (cur) => (same(cur) ? applyResolved(cur as LinkItem, preview) : cur));
      } catch (e) {
        const code = e instanceof ContentError ? e.code : "UNKNOWN";
        setRow(mediaKey, (cur) => (same(cur) ? ({ ...cur, status: "error", preview: undefined, error: code } as LinkItem) : cur));
      }
    }, resolveDelayMs));
  }

  async function onFiles(files: FileList | null) {
    const target = pick.current;
    if (!files || !target) return;
    const room = MAX_MEDIA - allMedia.length;
    const tiles = filesFromPicked(Array.from(files), room).map((tile) =>
      isVideoItem(tile) && tile.file ? { ...tile, file: undefined, uploadId: enqueueWallVideo(tile.file, draftId, tile.key) } : tile,
    );
    for (const { file: _f, ...tile } of tiles) addRow(target.paragraph, tile.type === "AUDIO" ? "AUDIO_URL" : tile.type, tile);
    for (const tile of tiles) {
      if (!tile.file) continue;
      const key = `m${tile.key}`;
      const set = (p: Partial<FileItem>) => setRow(key, (cur) => ({ ...cur, ...p } as ComposerItem));
      try {
        if (tile.type === "AUDIO") {
          set({ status: "done", fileId: (await uploadAudio(tile.file)).id });
          continue;
        }
        const res = await uploadImage(tile.file);
        set({ status: "done", fileId: res.id, previewUrl: res.url });
      } catch (err) {
        set({ status: "error", error: err instanceof SvgTooLargeError ? "image_svg_too_large" : "wall_photo_upload_failed" });
      }
    }
  }

  function openPicker(paragraph: string, kind: Kind) {
    if (kind === "link") return addLink(paragraph);
    pick.current = { paragraph, kind };
    const el = mediaInput.current;
    if (!el) return;
    el.accept = kind === "photo" ? IMAGE_ACCEPT : kind === "audio" ? AUDIO_ACCEPT : "video/mp4,video/quicktime,video/webm";
    el.click();
  }

  async function pickCover(file: File | undefined) {
    if (!file) return;
    setCoverFailed(false);
    setCoverBusy(true);
    try {
      const info = await uploadImage(file);
      patch({ cover: { fileId: info.id, url: info.url } }, "cover");
    } catch (err) {
      setCoverFailed(err instanceof SvgTooLargeError ? "svg" : "failed");
    } finally {
      setCoverBusy(false);
    }
  }

  async function submit() {
    if (submitting) return;
    setSubmitError(null);
    // A link row with no URL yet is left out, as in the wall composer.
    const isBlank = (it: ComposerItem | undefined) => it?.kind === "link" && it.url.trim() === "" && !it.preview && it.status !== "error";
    const files = views.filter((it): it is FileItem => it.kind === "file");
    const links = views.filter((it): it is LinkItem => it.kind === "link" && !isBlank(it));
    if (coverBusy || files.some((f) => f.status === "uploading")) return setSubmitError("BLOG_UPLOADING");
    if (files.some((f) => f.status === "error") || links.some((l) => l.status === "error")) return setSubmitError("BLOG_MEDIA_FAILED");
    if (links.some((l) => l.status !== "ok")) return setSubmitError("BLOG_LINK_PENDING");
    if (links.some(titleTooLong)) return setSubmitError("BLOG_LINK_TITLE");
    const final: BlogDraft = {
      ...draft,
      paragraphs: draft.paragraphs.map((pg) => ({
        ...pg,
        media: pg.media.filter((m) => !isBlank(rows[m.key])).map((m) => { const v = viewOf(m); return v ? mediaOf(m, v) : m; }),
      })),
    };
    const errs = blogErrors(final);
    if (Object.keys(errs).length > 0) {
      setShown(errs);
      return;
    }
    setSubmitting(true);
    submittingRef.current = true;
    try {
      const { title, description, blog, paragraphs } = toBlogInput(final);
      const input = { type: "BLOG" as const, title, description, blog, paragraphs };
      const saved = existing ? await updatePostV2(existing.id, input) : await createPostV2(input);
      invalidateNewsPosted(qc);
      // The videos are in the post: their queue items are done.
      releaseWallVideos(views.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : [])));
      onSaved(saved);
    } catch (e) {
      setSubmitError(e instanceof ContentError ? e.code : "UNKNOWN");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const words = wordCount(draft.abstract);
  const heading = existing ? t("blog_composer_edit", "Edit blog") : t("blog_composer_new", "New blog");
  const submitText =
    submitError === "BLOG_UPLOADING" ? t("blog_composer_wait_upload", "Wait for the uploads to finish")
      : submitError === "BLOG_MEDIA_FAILED" ? t("blog_composer_media_failed", "Remove the media that failed, or try again")
        : submitError === "BLOG_LINK_PENDING" ? t("blog_composer_link_pending", "Wait for the links to resolve")
          : submitError === "BLOG_LINK_TITLE" ? t("blog_composer_link_title", "A link title can have at most 120 characters")
          : submitError ? contentErrorText(t, submitError, t("wall_error_UNKNOWN", "Something went wrong — try again")) : null;

  const field = (id: string, label: string, opts: { value: string; on(v: string): void; err?: keyof BlogErrors; errText?: string; max?: number; placeholder?: string; area?: boolean; rowsN?: number; inputMode?: "numeric" }) => (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <label className={LABEL} htmlFor={`blog-composer-${id}`}>{label}</label>
      {opts.area ? (
        <textarea id={`blog-composer-${id}`} data-testid={`blog-composer-${id}`} className={`${INPUT} resize-y`} rows={opts.rowsN ?? 3} maxLength={opts.max}
          aria-invalid={opts.err && shown[opts.err] ? true : undefined} placeholder={opts.placeholder} value={opts.value}
          onChange={(e) => opts.on(e.target.value)} />
      ) : (
        <input id={`blog-composer-${id}`} data-testid={`blog-composer-${id}`} className={INPUT} maxLength={opts.max} inputMode={opts.inputMode}
          aria-invalid={opts.err && shown[opts.err] ? true : undefined} placeholder={opts.placeholder} value={opts.value}
          onChange={(e) => opts.on(e.target.value)} />
      )}
      {opts.err && shown[opts.err] && <Err id={id}>{opts.errText}</Err>}
    </div>
  );

  const abstractErr = words > MAX_ABSTRACT_WORDS
    ? t("blog_composer_abstract_over", { count: words - MAX_ABSTRACT_WORDS, defaultValue: "{{count}} words over the limit. Shorten the abstract to post." })
    : t("blog_composer_abstract_required", "Write an abstract");

  const discardDialog =
    pendingDiscard &&
    createPortal(
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
        <LiveConfirmDialog
          testId="discard-draft-dialog"
          zClass="z-[2200]"
          message={t("wall_discard_draft_message", "Discard this post? Your changes will be lost.")}
          confirmLabel={t("wall_discard_draft_confirm", "Discard")}
          cancelLabel={t("wall_discard_draft_keep", "Keep editing")}
          confirmTestId="discard-draft-confirm"
          onConfirm={() => useComposerDraftStore.getState().confirmDiscard()}
          onCancel={() => useComposerDraftStore.getState().cancelDiscard()}
        />
      </div>,
      document.body,
    );

  const kinds: { id: Kind; label: string }[] = [
    { id: "photo", label: t("blog_composer_add_photo", "Photo") },
    { id: "video", label: t("blog_composer_add_video", "Video") },
    { id: "audio", label: t("blog_composer_add_audio", "Audio") },
    { id: "link", label: t("blog_composer_add_link", "Link") },
  ];

  const paragraphCard = (pg: BlogParagraphDraft, i: number) => {
    const bad = shown.badParagraphs?.includes(i);
    const textOver = [...pg.body.trim()].length > MAX_PARAGRAPH_CHARS;
    return (
      <div key={pg.key} data-testid="blog-paragraph-card" className={`flex flex-col gap-2 rounded-xl border p-3 ${bad ? "border-[var(--destructive)]" : "border-[var(--border)]"}`}>
        {draft.paragraphs.length > 1 && <div className="flex items-center justify-end gap-1">
          <button type="button" data-testid="blog-paragraph-up" aria-label={t("blog_composer_move_up", "Move paragraph up")} disabled={i === 0} className={ICON_BTN}
            onClick={() => patch(moveParagraphUp(draft, i))}><ArrowUp size={16} aria-hidden /></button>
          <button type="button" data-testid="blog-paragraph-down" aria-label={t("blog_composer_move_down", "Move paragraph down")} disabled={i === draft.paragraphs.length - 1} className={ICON_BTN}
            onClick={() => patch(moveParagraphDown(draft, i))}><ArrowDown size={16} aria-hidden /></button>
          <button type="button" data-testid="blog-paragraph-remove" aria-label={t("blog_composer_remove_paragraph", "Remove paragraph")} disabled={draft.paragraphs.length <= 1} className={ICON_BTN}
            onClick={() => {
              const gone = pg.media.map((m) => m.key);
              gone.forEach(removeMedia);
              patch({ paragraphs: draft.paragraphs.filter((p) => p.key !== pg.key) });
            }}><Trash2 size={16} aria-hidden /></button>
        </div>}
        <textarea data-testid="blog-paragraph-text" className={`${INPUT} resize-y bg-[var(--bg-elevated)]`} rows={3} aria-label={t("blog_composer_paragraph", "Paragraph")}
          placeholder={t("blog_composer_paragraph_ph", "Write a paragraph")} value={pg.body}
          onChange={(e) => patchParagraph(pg.key, (p) => ({ ...p, body: e.target.value }))} />
        {bad && <Err id="paragraph">{textOver ? t("blog_composer_paragraph_long", "A paragraph can have at most 10,000 characters") : t("blog_composer_paragraph_empty", "Add text or media to this paragraph")}</Err>}
        {pg.media.map((m, j) => {
          const view = viewOf(m);
          if (!view) return null;
          const capBad = shown.badCaptions?.some((c) => c.paragraph === i && c.media === j);
          return (
            <div key={m.key} data-testid="blog-media-row" className="flex items-start gap-1">
              <div className="min-w-0 flex-1">
                <ComposerItemRow
                  item={view} index={j} total={pg.media.length} hideGrip
                  onRemove={() => removeMedia(m.key)}
                  onUrlChange={(u) => onUrlChange(m.key, u)}
                  onTitleChange={(x) => setRow(m.key, (cur) => (cur.kind === "link" ? editTitle(cur, x) : cur))}
                  onTitleReset={() => setRow(m.key, (cur) => (cur.kind === "link" ? resetTitle(cur) : cur))}
                  onRetry={view.kind === "file" && view.uploadId != null ? () => uploadQueue.retry(view.uploadId!) : undefined}
                  onCaptionChange={(c) => patchParagraph(pg.key, (p) => ({ ...p, media: p.media.map((x) => (x.key === m.key ? { ...x, caption: c.slice(0, MAX_CAPTION) } : x)) }))}
                />
                {capBad && <Err id="caption">{t("blog_composer_caption_long", "A caption can have at most 500 characters")}</Err>}
              </div>
              <button type="button" data-testid="blog-media-up" aria-label={t("blog_composer_media_up", "Move media up")} className={ICON_BTN}
                onClick={() => patchParagraph(pg.key, (p) => moveMediaUp(p, j))}><ArrowUp size={16} aria-hidden /></button>
              <button type="button" data-testid="blog-media-down" aria-label={t("blog_composer_media_down", "Move media down")} className={ICON_BTN}
                onClick={() => patchParagraph(pg.key, (p) => moveMediaDown(p, j))}><ArrowDown size={16} aria-hidden /></button>
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[var(--foreground-muted)]">{t("blog_composer_add", "Add")}</span>
          {kinds.map((k) => (
            <button key={k.id} type="button" data-testid={`blog-add-media-${k.id}`} disabled={allMedia.length >= MAX_MEDIA} className={PILL} onClick={() => openPicker(pg.key, k.id)}>
              {k.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={heading} data-testid="blog-composer"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]"
      onClick={() => guardedCloseRef.current()}>
      <div className="flex max-h-[92vh] w-[720px] max-w-full flex-col gap-3 overflow-y-auto rounded-2xl bg-[var(--bg-surface)] p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center gap-4">
          <h2 className="m-0 flex-1 text-lg font-bold text-[var(--foreground)]">{heading}</h2>
          <button type="button" aria-label={t("wall_close", "Close")} disabled={submitting} className="text-[var(--foreground)] cursor-pointer disabled:opacity-45" onClick={() => guardedCloseRef.current()}>
            <X size={24} aria-hidden />
          </button>
        </header>

        {field("title", t("blog_composer_title", "Title"), { value: draft.title, on: (v) => patch({ title: v }, "title"), err: "title", errText: t("blog_composer_title_required", "Add a title"), max: 120, placeholder: t("blog_composer_title", "Title") })}
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between">
            <label className={LABEL} htmlFor="blog-composer-abstract">{t("blog_composer_abstract", "Abstract")}</label>
            <span data-testid="blog-composer-abstract-count" className={`text-[11px] ${words > MAX_ABSTRACT_WORDS ? "text-[var(--destructive-text)]" : "text-[var(--foreground-muted)]"}`}>
              {t("blog_composer_abstract_count", { count: words, max: MAX_ABSTRACT_WORDS, defaultValue: "{{count}} / {{max}} words" })}
            </span>
          </div>
          <textarea id="blog-composer-abstract" data-testid="blog-composer-abstract" className={`${INPUT} resize-y`} rows={4} maxLength={4096}
            aria-invalid={shown.abstract ? true : undefined} placeholder={t("blog_composer_abstract_ph", "Write a 500 words abstract")} value={draft.abstract}
            onChange={(e) => patch({ abstract: e.target.value }, "abstract")} />
          {shown.abstract && <Err id="abstract">{abstractErr}</Err>}
        </div>

        <div className="flex flex-col gap-1">
          <span className={LABEL}>{t("blog_composer_cover", "Cover")}</span>
          {draft.cover ? (
            <>
              <img data-testid="blog-composer-cover-preview" src={draft.cover.url} alt="" className="h-[84px] w-full rounded-xl object-cover" />
              <button type="button" data-testid="blog-composer-cover" disabled={coverBusy} className={`${PILL} self-start`} onClick={() => coverInput.current?.click()}>
                {t("blog_composer_cover_change", "Change cover")}
              </button>
            </>
          ) : (
            <button type="button" data-testid="blog-composer-cover" disabled={coverBusy} onClick={() => coverInput.current?.click()}
              className="flex h-[84px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] text-sm text-[var(--foreground)] cursor-pointer">
              <ImagePlus size={22} aria-hidden />
              {coverBusy ? t("blog_composer_cover_uploading", "Uploading…") : t("blog_composer_cover_add", "Add a cover")}
            </button>
          )}
          <input data-testid="blog-composer-cover-input" ref={coverInput} type="file" accept={IMAGE_ACCEPT} hidden
            onChange={(e) => { void pickCover(e.target.files?.[0]); e.target.value = ""; }} />
          {shown.cover && !draft.cover && <Err id="cover">{t("blog_composer_cover_required", "Add a cover")}</Err>}
          {coverFailed && <Err id="cover-upload">{coverFailed === "svg" ? t("image_svg_too_large", "This SVG is over 1 MB") : t("blog_composer_cover_failed", "The cover didn't upload — try again")}</Err>}
        </div>

        <div className="flex flex-wrap gap-3">
          {field("author", t("blog_composer_author", "Original Author"), { value: draft.authorName, on: (v) => patch({ authorName: v }, "authorName"), err: "authorName", errText: t("blog_composer_author_required", "Add the original author"), max: MAX_AUTHOR, placeholder: t("blog_composer_author_ph", "Original author") })}
          {field("year", t("blog_composer_year", "Year"), { value: draft.year, on: (v) => patch({ year: v }, "year"), err: "year", errText: t("blog_composer_year_invalid", "Enter a year, e.g. 1999"), max: 4, inputMode: "numeric", placeholder: t("blog_composer_year", "Year") })}
          {field("language", t("blog_composer_language", "Language"), { value: draft.language, on: (v) => patch({ language: v }, "language"), err: "language", errText: t("blog_composer_language_required", "Add the language"), max: MAX_LANGUAGE, placeholder: t("blog_composer_language", "Language") })}
        </div>
        <div className="flex flex-wrap gap-3">
          {field("translator", t("blog_composer_translator", "Translator"), { value: draft.translator, on: (v) => patch({ translator: v }, "translator"), max: MAX_TRANSLATOR, placeholder: t("blog_composer_optional", "Optional") })}
          {field("translation-language", t("blog_composer_translation_language", "Translation language"), { value: draft.translationLanguage, on: (v) => patch({ translationLanguage: v }, "translationLanguage"), max: MAX_TRANSLATION_LANGUAGE, placeholder: t("blog_composer_optional", "Optional") })}
        </div>

        <h3 className="m-0 text-base font-bold text-[var(--foreground)]">{draft.paragraphs.length > 1 ? t("blog_composer_paragraphs", "Paragraphs") : t("blog_composer_paragraph", "Paragraph")}</h3>
        {draft.paragraphs.map(paragraphCard)}
        {(shown.paragraphs || shown.text || shown.media) && (
          <Err id="paragraphs">
            {shown.media ? t("blog_composer_media_limit", "A blog can have at most 200 media")
              : shown.text ? t("blog_composer_text_limit", "The text is over 200,000 characters in total")
                : t("blog_composer_paragraph_count", "A blog has 1 to 100 paragraphs")}
          </Err>
        )}
        <input data-testid="blog-media-input" ref={mediaInput} type="file" multiple hidden
          onChange={(e) => { void onFiles(e.target.files); e.target.value = ""; }} />

        {field("acknowledgements", t("blog_composer_acknowledgements", "Acknowledgements"), { value: draft.acknowledgements, on: (v) => patch({ acknowledgements: v }, "acknowledgements"), err: "acknowledgements", max: MAX_ACKNOWLEDGEMENTS, area: true, rowsN: 2, placeholder: t("blog_composer_acknowledgements_ph", "Thank the people who helped") })}
        {field("bibliography", t("blog_composer_bibliography", "Bibliography"), { value: draft.bibliography, on: (v) => patch({ bibliography: v }, "bibliography"), err: "bibliography", max: MAX_BIBLIOGRAPHY, area: true, rowsN: 2, placeholder: t("blog_composer_bibliography_ph", "Sources, one per line") })}

        {submitText && <p data-testid="blog-composer-submit-error" role="alert" className="m-0 text-sm text-[var(--destructive-text)]">{submitText}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" data-testid="blog-composer-cancel" disabled={submitting} className={`${PILL} h-10 px-6`} onClick={() => guardedCloseRef.current()}>{t("wall_cancel", "Cancel")}</button>
          <button type="button" data-testid="blog-composer-submit" aria-busy={submitting} onClick={() => void submit()}
            className="h-10 rounded-full bg-[var(--primary)] px-6 text-sm font-bold text-[var(--primary-foreground)] aria-busy:opacity-60 cursor-pointer">
            {existing ? t("wall_save_button", "Save") : t("wall_post_button", "Post")}
          </button>
        </div>
      </div>
      {discardDialog}
    </div>
  );
}
